import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Link } from "react-router-dom";
import { Check, Filter } from "lucide-react";
import { motion } from "motion/react";
import { AriBrandAvatar, AriBrandOrFallback } from "@/components/demo-ari/AriBrandIcon";
import { AriIcon } from "@/components/sidebar/icons";
import { resolveAriBrandId } from "@/lib/ariBrands";
import { Button } from "@/components/ui/Button";
import { cn } from "@/utils/Functions";
import { ARI_DEAL_LABEL_COLORS } from "@/constants/deal.constants";
import { resolveAriDealRoomHref } from "@/mockData/ariPipeline";
import { formatCurrency } from "@/views/deals/utils";
import type { AriDealLabelId, Deal } from "@/types/deal.types";

interface AriDealKanbanCardProps {
  deal: Deal;
  isDragging?: boolean;
}

function contactInitial(name: string) {
  return name.trim().charAt(0).toUpperCase() || "?";
}

export function AriDealKanbanCard({ deal, isDragging }: AriDealKanbanCardProps) {
  const dealHref = resolveAriDealRoomHref(deal.id);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging: isSortableDragging } = useSortable({
    id: deal.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const actualIsDragging = isDragging || isSortableDragging;
  const labelId = deal.dealLabelId as AriDealLabelId | undefined;
  const labelConfig = labelId ? ARI_DEAL_LABEL_COLORS[labelId] : null;
  const brandId =
    deal.brandId ?? resolveAriBrandId(deal.name, deal.contactCompany, deal.contactName);

  return (
    <motion.div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={cn(
        "group cursor-grab rounded-lg border border-border/80 bg-card p-3 transition-all duration-150",
        "hover:border-border hover:shadow-sm",
        actualIsDragging && "cursor-grabbing opacity-50 shadow-lg",
      )}
    >
      <Link
        to={dealHref}
        className="mb-2 line-clamp-2 block text-[13px] font-semibold leading-snug text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        {deal.name}
      </Link>

      {deal.contactName && (
        <div className="mb-1.5 flex items-center gap-1.5">
          <span className="flex size-[18px] shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground text-[0.65rem]">
            {contactInitial(deal.contactName)}
          </span>
          <span className="truncate text-[11.5px] text-muted-foreground">{deal.contactName}</span>
        </div>
      )}

      {deal.contactCompany && (
        <div className="mb-2 flex items-center gap-1.5">
          <AriBrandOrFallback
            brandId={brandId}
            size="xs"
            fallback={
              <span className="flex size-[18px] shrink-0 items-center justify-center rounded-full bg-muted text-[9px] font-semibold text-muted-foreground">
                {contactInitial(deal.contactCompany)}
              </span>
            }
          />
          <span className="truncate text-[11.5px] text-muted-foreground">{deal.contactCompany}</span>
        </div>
      )}

      <div className="mb-2 flex items-center gap-1.5">
        <div className={cn("rounded-full size-5 grid place-items-center", deal.nextTask ? "bg-primary" : "bg-primary/50")}>
          <Check
            className={cn("size-3 shrink-0", deal.nextTask ? "text-primary-foreground" : "text-primary-foreground/")}
            aria-hidden="true"
          />
        </div>  
        <span
          className={cn(
            "truncate text-[11.5px]",
            deal.nextTask ? "text-muted-foreground" : "italic text-muted-foreground/60",
          )}
        >
          {deal.nextTask || "No Next task"}
        </span>
      </div>

      {deal.value > 0 && (
        <div className="mb-1 text-xs font-semibold text-[#22c55e]">{formatCurrency(deal.value)}</div>
      )}

      {deal.dueDate && <div className="mb-1 text-[11px] text-muted-foreground">{deal.dueDate}</div>}

      {labelConfig && (
        <span
          className={cn(
            "mt-1 inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-wide",
            labelConfig.className,
          )}
        >
          {labelConfig.name}
        </span>
      )}

    </motion.div>
  );
}

export function AriSuggestedDealCard({
  deal,
  onAdd,
}: {
  deal: Deal;
  onAdd: (deal: Deal) => void;
}) {
  const labelId = deal.dealLabelId as AriDealLabelId | undefined;
  const labelConfig = labelId ? ARI_DEAL_LABEL_COLORS[labelId] : null;
  const dealHref = resolveAriDealRoomHref(deal.id);
  const brandId =
    deal.brandId ?? resolveAriBrandId(deal.name, deal.contactCompany, deal.contactName);

  return (
    <div className="w-[220px] shrink-0 rounded-lg border border-border bg-card p-3">
      <div className="mb-1 flex items-center gap-1.5">
        {brandId ? <AriBrandAvatar brandId={brandId} size="xs" /> : null}
        <Link
          to={dealHref}
          className="min-w-0 flex-1 truncate text-xs font-medium text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
        >
          {deal.name}
        </Link>
      </div>
      <div className="mb-2 truncate text-[11.5px] text-muted-foreground">{deal.contactName}</div>
      <div className="flex items-center gap-2">
        {labelConfig && (
          <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-semibold", labelConfig.className)}>
            {labelConfig.name}
          </span>
        )}
        <button
          type="button"
          onClick={() => onAdd(deal)}
          className="ml-auto rounded bg-[#4d6af5] px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-[#4d6af5]/90"
        >
          + Add
        </button>
      </div>
    </div>
  );
}

export function AriPipelineToolbar() {
  return (
    <div className="flex shrink-0 items-center gap-2 border-b border-border bg-card/40 px-5 py-2.5">
      <Button variant="outline" size="sm" className="gap-1.5">
        <AriIcon variant="gradient" className="size-3" aria-hidden="true" />
        AI Search
      </Button>
      <Button variant="outline" size="sm" className="gap-1.5">
        <Filter className="size-3" aria-hidden="true" />
        Filter
      </Button>
      <Button variant="outline" size="sm">
        Created at
      </Button>
      <Button variant="outline" size="sm">
        Stage
      </Button>
    </div>
  );
}
